import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format, parseISO, addDays, subDays, isToday, isSameDay } from "date-fns";
import { FABadge, isFAImported } from "@/components/app/FABadge";
import { AgendaView } from "@/components/calendar/AgendaView";
import { BookingDetailDialog } from "@/components/calendar/BookingDetailDialog";
import { NewBookingDialog } from "@/components/calendar/NewBookingDialog";
import { SecretaryNoticeDialog } from "@/components/calendar/SecretaryNoticeDialog";

const FORMATS = ["3v3", "5v5", "7v7", "9v9", "11v11"];

// Map team colour → tailwind classes for booked cells
const TEAM_COLOR = {
  // Arbitrary-value colours (`text-[#ffffff]`) are always picked up by Tailwind's JIT
  // scanner AND aren't touched by the light-mode `.text-white` remap in `index.css`,
  // so the vivid team-coloured booking cells keep their designed contrast in every theme.
  Black:  { bg: "bg-black",           text: "text-[#ffffff]",     border: "border-white/20" },
  White:  { bg: "bg-white",           text: "text-[#000000]",     border: "border-black/20" },
  Green:  { bg: "bg-emerald-500",     text: "text-[#ffffff]",     border: "border-emerald-700" },
  Yellow: { bg: "bg-[#FACC15]",      text: "text-[#000000]",     border: "border-[#CA8A04]" },
  Blue:   { bg: "bg-blue-500",        text: "text-[#ffffff]",     border: "border-blue-700" },
};
const DEFAULT_COLOR = { bg: "bg-[#83B68B]", text: "text-white", border: "border-[#83B68B]" };

export default function VenueCalendar() {
  const { user, siteSettings } = useAuth();
  const secretaryEnabled = siteSettings?.secretary_notices_enabled !== false;
  const prefsKey = user?.id ? `venueCal:${user.id}` : null;
  const initialPrefs = (() => {
    try { return prefsKey ? JSON.parse(localStorage.getItem(prefsKey) || "{}") : {}; }
    catch { return {}; }
  })();
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState(initialPrefs.venueId || "");
  const [venueMode, setVenueMode] = useState(initialPrefs.venueMode || "game_day");
  const [format_, setFormat] = useState(initialPrefs.format || "all");
  const [status, setStatus] = useState(initialPrefs.status || "all");
  const [viewMode, setViewMode] = useState(initialPrefs.viewMode || (typeof window !== "undefined" && window.innerWidth < 640 ? "agenda" : "grid"));
  const [ageGroup, setAgeGroup] = useState(initialPrefs.ageGroup || "all");
  const [teamFilterId, setTeamFilterId] = useState(initialPrefs.teamFilterId || "all");
  const [trainingSeason, setTrainingSeason] = useState(initialPrefs.trainingSeason || "all");
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [secretaryNotice, setSecretaryNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [kitLabels, setKitLabels] = useState({Black:"Black", White:"White", Green:"Green", Yellow:"Yellow", Blue:"Blue"});
  useEffect(() => { api.get("/settings/kit-labels").then(r => setKitLabels({...{Black:"Black",White:"White",Green:"Green",Yellow:"Yellow",Blue:"Blue"}, ...(r.data.labels||{})})).catch(()=>{}); }, []);

  // Click-to-book state
  const [teams, setTeams] = useState([]);        // filtered teams (owned / all-club) — booking dropdown
  const [allTeams, setAllTeams] = useState([]);  // ALL club teams — powers the FILTER dropdowns for every user
  const [referees, setReferees] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [newSlot, setNewSlot] = useState(null); // { pitch, hour, min }
  const [clubSettings, setClubSettings] = useState(null); // for winter/summer season auto-classify
  useEffect(() => { api.get("/admin/settings").then(r => setClubSettings(r.data)).catch(() => setClubSettings({})); }, []);
  const [nb, setNb] = useState({ duration: 60, session_type: "training", match_type: "league", team_id: "", referee_id: "", opponent: "", notes: "", repeat_weeks: 1, shared_team_ids: [], warm_up_min: 0, cool_down_min: 0 });
  const [nbBusy, setNbBusy] = useState(false);
  const [clashDetail, setClashDetail] = useState(null);
  // Admin default-scope toggle: "Mine" vs "All club" teams in booking dropdown
  const [showAllTeams, setShowAllTeams] = useState(false);

  // Sidebar collapse (hides Venue/Date/Filters to give the grid full width)
  const [showSidebar, setShowSidebar] = useState(initialPrefs.showSidebar ?? false);
  const [refView, setRefView] = useState(initialPrefs.refView ?? false);
  const [refereeBusy, setRefereeBusy] = useState([]);

  // Persist filter/sidebar prefs whenever they change
  useEffect(() => {
    if (!prefsKey) return;
    try { localStorage.setItem(prefsKey, JSON.stringify({ venueId, venueMode, format: format_, status, ageGroup, teamFilterId, trainingSeason, showSidebar, viewMode, refView })); }
    catch (err) { console.warn("Failed to persist calendar prefs:", err); }
  }, [prefsKey, venueId, venueMode, format_, status, ageGroup, teamFilterId, trainingSeason, showSidebar, viewMode, refView]);

  // Drag-select state (mouse-based, for creating a NEW booking by dragging across empty cells)
  const [drag, setDrag] = useState(null); // { pitchId, startMin, currentMin }
  const filteredRef = useRef(null);

  // HTML5 drag-drop state (dragging an EXISTING booking to a new slot on the same day)
  // Persisted via useRef so it survives re-renders during the drag; a lightweight
  // `dragOver` state powers the visual drop-target highlight.
  const dragBookingRef = useRef(null); // { booking_id, duration_min, from_pitch_id, from_start_min }
  const [dragOver, setDragOver] = useState(null); // { pitchId, startMin }

  useEffect(() => {
    const onUp = () => { setDrag(d => {
      if (d && d.startMin != null && d.currentMin != null && d.startMin !== d.currentMin) {
        const f = filteredRef.current;
        const pitch = f?.rows.find(r => r.pitch.id === d.pitchId)?.pitch;
        if (pitch) {
          const [a, b] = d.startMin <= d.currentMin ? [d.startMin, d.currentMin] : [d.currentMin, d.startMin];
          const duration = (b - a) + 15;
          setNewSlot({ pitch, hour: Math.floor(a/60), min: a % 60 });
          setNb(prev => ({ ...prev, duration }));
        }
      }
      return null;
    }); };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  useEffect(() => { (async () => {
    try {
      const teamsUrl = showAllTeams ? "/teams" : "/teams?owned_only=true";
      const [t, r, p] = await Promise.all([api.get(teamsUrl), api.get("/referees"), api.get("/pitches")]);
      setTeams(t.data); setReferees(r.data); setPitches(p.data);
    } catch (err) { console.warn("Failed to preload teams/referees/pitches:", err); }
  })(); }, [showAllTeams]);

  // ALL club teams — powers the calendar's age/team FILTER dropdowns for every user
  // (viewing bookings is club-wide; booking uses the owned-only `teams` list above).
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/teams");
      setAllTeams(data);
    } catch (err) { console.warn("Failed to preload all-club teams:", err); }
  })(); }, []);

  // Highest-age filter for the referee dropdown. A referee with max_age_group="U12"
  // can only officiate up to U12 games — hide them when a higher-age team is picked.
  const selectedTeam = teams.find(t => t.id === nb.team_id);
  const dateStr = useMemo(() => format(date, "yyyy-MM-dd"), [date]);
  const loadRefereeBusy = useCallback(async () => {
    try { const { data } = await api.get("/referees/busy", { params: { date: dateStr } }); setRefereeBusy(data); } catch { setRefereeBusy([]); }
  }, [dateStr]);
  useEffect(() => { loadRefereeBusy(); }, [loadRefereeBusy, openId, newSlot]);
  // All active referees are listed; RefereeSelectItems greys out ineligible ones with the reason.
  const eligibleReferees = referees;

  const openNewBooking = (pitch, s) => {
    if (user?.role === "member" || user?.role === "vendor") { toast.error("View only — this account can't make bookings"); return; }
    // Smart-default duration: cap at the gap before the next booked slot on this pitch/day
    // so clicking a slot with only 30 min before the next fixture doesn't try (and fail) to book 60 min.
    let defaultDuration = 60;
    const row = filteredRef.current?.rows.find(r => r.pitch.id === pitch.id);
    if (row) {
      const nextBooked = row.slots.find(slot => slot.minute > s.minute && slot.booked);
      const closeMinute = pitch.close_hour * 60;
      const capMinute = nextBooked ? nextBooked.minute : closeMinute;
      const freeMinutes = capMinute - s.minute;
      if (freeMinutes < defaultDuration) defaultDuration = Math.max(15, freeMinutes);
    }
    setNewSlot({ pitch, hour: s.hour, min: s.min });
    // Auto-classify the training_season based on Admin's configured Winter/Summer start dates.
    // If neither is configured yet we leave it as "both" — the coach can still change it.
    const winterStart = clubSettings?.winter_training_start;
    const summerStart = clubSettings?.summer_training_start;
    const bookMMDD = format(date, "MM-dd");
    let seasonDefault = "both";
    if (winterStart && summerStart) {
      const wStart = winterStart.slice(5); // MM-DD
      const sStart = summerStart.slice(5);
      // If winter starts later in the year (typical UK: Oct → Mar), season is winter when
      // date >= winter start OR date < summer start.
      if (wStart >= sStart) {
        seasonDefault = (bookMMDD >= wStart || bookMMDD < sStart) ? "winter" : "summer";
      } else {
        seasonDefault = (bookMMDD >= wStart && bookMMDD < sStart) ? "winter" : "summer";
      }
    }
    setNb({ duration: defaultDuration, session_type: "training", match_type: "league", team_id: "", age_group: "", book_mode: "team", referee_id: "", opponent: "", notes: "", repeat_weeks: 1, shared_team_ids: [], warm_up_min: 0, cool_down_min: 0, training_season: seasonDefault });
  };

  const submitNewBooking = async () => {
    if (!newSlot) return;
    const yearGroupMode = nb.session_type === "training" && nb.book_mode === "yeargroup";
    if (yearGroupMode) {
      if (!nb.age_group) return toast.error("Please pick an age group for this training");
    } else {
      if (!nb.team_id) return toast.error("Please pick a team for this booking");
    }
    // For games, the actual booked window includes optional warm-up before + cool-down after
    // so the pitch is blocked out for the full session, not just kick-off → full-time.
    const warmUp  = nb.session_type === "game" ? (nb.warm_up_min  || 0) : 0;
    const coolDown = nb.session_type === "game" ? (nb.cool_down_min || 0) : 0;
    const startTotal = newSlot.hour * 60 + newSlot.min - warmUp;
    const endTotal   = newSlot.hour * 60 + newSlot.min + nb.duration + coolDown;
    if (startTotal < (data?.open_hour || 0) * 60) return toast.error("Warm-up starts before pitch opens — reduce warm-up or pick a later slot");
    if (endTotal > (data?.close_hour || 24) * 60) return toast.error("Cool-down ends past pitch closing — reduce cool-down or shorten the session");
    const refNeeded = nb.session_type === "game" && (nb.match_type === "league" || nb.match_type === "cup");
    if (refNeeded && !nb.referee_id && !nb.referee_tbd) return toast.error("Referee is required for league/cup matches (or tick 'Referee TBD')");
    setNbBusy(true);
    setClashDetail(null);
    try {
      await api.post("/bookings", {
        pitch_id: newSlot.pitch.id,
        booking_date: dateStr,
        start_hour: Math.floor(startTotal / 60),
        start_min: startTotal % 60,
        end_hour: Math.floor(endTotal / 60),
        end_min: endTotal % 60,
        team_id: yearGroupMode ? null : nb.team_id,
        age_group: yearGroupMode ? nb.age_group : null,
        notes: nb.notes || null,
        session_type: nb.session_type,
        match_type: nb.session_type === "game" ? nb.match_type : null,
        referee_id: refNeeded && !nb.referee_tbd ? nb.referee_id : null,
        referee_tbd: refNeeded ? !!nb.referee_tbd : false,
        referee_actual_name: nb.referee_actual_name || null,
        opponent: nb.session_type === "game" ? (nb.opponent || null) : null,
        repeat_weeks: parseInt(nb.repeat_weeks) || 1,
        shared_team_ids: nb.session_type === "training" && !yearGroupMode ? (nb.shared_team_ids || []).filter(Boolean).slice(0, 3) : null,
        training_season: nb.session_type === "training" ? (nb.training_season || "both") : null,
      });
      toast.success(nb.repeat_weeks > 1 ? `${nb.repeat_weeks} weekly bookings requested` : "Booking confirmed");
      setNewSlot(null);
      // reload availability
      const { data: fresh } = await api.get("/venues/availability", { params: { venue_id: venueId, date_str: dateStr } });
      setData(fresh);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Slot-clash 409 → keep the dialog open and surface the rich detail (team +
      // coach + contact) in the amber panel at the top. All other errors stay
      // as a normal toast.
      if (detail && typeof detail === "object" && detail.code === "slot_clash") {
        setClashDetail(detail);
      } else {
        toast.error(formatApiError(detail) || "Booking failed");
      }
    } finally { setNbBusy(false); }
  };

  useEffect(() => { (async () => {
    const { data } = await api.get("/venues");
    setVenues(data);
    // Only auto-pick a venue if none is saved / restored. Game Day mode defaults to all game-day venues.
    if (!venueId) {
      if (data.some(v => v.is_game_day)) { setVenueId("all"); return; }
      setVenueMode("all");
      const withPitches = data.find(v => (v.pitches || []).length > 0) || data[0];
      if (withPitches) setVenueId(withPitches.id);
    }
  })(); }, []);

  const gameDayOnly = venueMode === "game_day";
  const visibleVenues = gameDayOnly ? venues.filter(v => v.is_game_day) : venues;
  const switchVenueMode = (mode) => {
    setVenueMode(mode);
    if (mode === "game_day" && venueId !== "all" && !venues.find(v => v.id === venueId)?.is_game_day) setVenueId("all");
  };

  const [closedNotice, setClosedNotice] = useState(null);
  useEffect(() => { (async () => {
    if (!venueId) return;
    setLoading(true);
    setClosedNotice(null);
    try {
      const params = { venue_id: venueId, date_str: dateStr };
      if (venueId === "all" && gameDayOnly) params.game_day_only = true;
      // Only pass the season param when the user has actually picked one — "all" means don't filter.
      if (trainingSeason === "winter" || trainingSeason === "summer") params.training_season = trainingSeason;
      const { data } = await api.get("/venues/availability", { params });
      setData(data);
    } catch (err) {
      // 410 means the venue is closed today or unavailable — show a friendly banner
      // instead of a red error toast so the user knows to pick a different day.
      if (err.response?.status === 410) {
        setData(null);
        setClosedNotice(err.response?.data?.detail || "Venue is closed on this day.");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load calendar");
      }
    } finally { setLoading(false); }
  })(); }, [venueId, dateStr, trainingSeason, gameDayOnly]);

  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    setDetailLoading(true);
    api.get(`/bookings/${openId}`).then(r => setDetail(r.data)).finally(() => setDetailLoading(false));
  }, [openId]);

  const currentVenue = venues.find(v => v.id === venueId);
  const isAll = venueId === "all";

  // Auto-clear an incompatible team selection when the age chip changes.
  // If the user picked "Palmer · U11" and then swaps to age U8, we drop the
  // team filter back to "All" so the calendar isn't silently blank.
  useEffect(() => {
    if (teamFilterId === "all" || ageGroup === "all") return;
    const team = allTeams.find(t => t.id === teamFilterId);
    if (team && team.age_group !== ageGroup) setTeamFilterId("all");
  }, [ageGroup, teamFilterId, allTeams]);

  const filtered = useMemo(() => {
    if (!data) return null;
    const rows = data.rows.filter(r => format_ === "all" ? true : (r.pitch.game_format === format_));
    const projected = rows.map(r => {
      const slots = r.slots.map(s => {
        if (!s.open) return s;
        let dim = false;
        if (status === "free") dim = dim || s.booked;
        if (status === "booked") dim = dim || !s.booked;
        if (status === "shared") dim = dim || !(s.booked && (s.shared_count || 0) > 0);
        if (ageGroup !== "all") dim = dim || !(s.booked && s.age_group === ageGroup);
        if (teamFilterId !== "all") {
          const teamName = allTeams.find(t => t.id === teamFilterId)?.name;
          const inShared = s.booked && Array.isArray(s.shared_team_names) && teamName && s.shared_team_names.includes(teamName);
          dim = dim || !(s.booked && (s.team_name === teamName || inShared));
        }
        return { ...s, _dim: dim };
      });
      return { ...r, slots };
    });
    return { ...data, rows: projected };
  }, [data, format_, status, ageGroup, teamFilterId, allTeams]);

  // Age options come from the FULL club roster (not just today's bookings) so
  // the filter list is complete even on an empty day. Sort natural U-order.
  const ageOptions = useMemo(() => {
    const set = new Set();
    allTeams.forEach(t => { if (t.age_group) set.add(t.age_group); });
    // Also fold in any age groups seen in today's data — covers rare bookings on retired teams.
    if (data) data.rows.forEach(r => r.slots.forEach(s => { if (s.age_group) set.add(s.age_group); }));
    const AGE_ORDER = ["U6","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","Adults"];
    return Array.from(set).sort((a,b) => AGE_ORDER.indexOf(a) - AGE_ORDER.indexOf(b));
  }, [allTeams, data]);

  const summary = useMemo(() => {
    if (!filtered) return null;
    let total = 0, booked = 0;
    filtered.rows.forEach(r => r.slots.forEach(s => {
      if (!s.open) return;
      total += 1;
      if (s.booked) booked += 1;
    }));
    return { total, booked, free: total - booked };
  }, [filtered]);
  useEffect(() => { filteredRef.current = filtered; }, [filtered]);

  // Hours header: derive unique hour marks from slot minute stream
  const hours = filtered ? Array.from({length: filtered.close_hour - filtered.open_hour}, (_, i) => filtered.open_hour + i) : [];
  const quartersPerHour = 4;
  // Each row = pitch label (narrower on phones) + hours*4 quarter columns
  const cellWidth = "minmax(18px, 1fr)";
  const isPhone = typeof window !== "undefined" && window.innerWidth < 640;
  const labelCol = isPhone ? "120px" : "200px";
  const gridCols = `${labelCol} repeat(${hours.length * quartersPerHour}, ${cellWidth})`;

  const colorFor = (s) => {
    if (!s.booked) return null;
    return TEAM_COLOR[s.team_color] || DEFAULT_COLOR;
  };
  const myTeamIds = useMemo(() => new Set((allTeams.length ? allTeams : teams).filter(t => t.owner_id === user?.id || (t.coach_ids || []).includes(user?.id)).map(t => t.id)), [allTeams, teams, user?.id]);
  const isMine = (s) => !!s.booked && !!user && (s.user_id === user.id || myTeamIds.has(s.team_id) || (s.shared_team_ids || []).some(id => myTeamIds.has(id)));
  const refTotals = useMemo(() => {
    if (!refView || !filtered) return [];
    const seen = new Set(); const by = {};
    for (const r of filtered.rows || []) for (const s of r.slots || []) {
      if (!s.booked || s.session_type !== "game" || !s.referee_name || seen.has(s.booking_id)) continue;
      seen.add(s.booking_id);
      const k = s.referee_name; by[k] = by[k] || { name: k, games: 0, total: 0, missing: 0 };
      by[k].games += 1; if (s.referee_fee != null) by[k].total += Number(s.referee_fee); else by[k].missing += 1;
    }
    return Object.values(by).sort((a, b) => b.total - a.total);
  }, [refView, filtered]);

  return (
    <div>
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Availability</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Venue calendar</h1>
        <p className="opacity-60 mt-2 text-sm"><span className="hidden sm:inline">15-minute resolution · cells coloured by team kit · click a booking for full details.</span><span className="sm:hidden">Agenda lists the day's bookings per pitch — tap a booking for details or a free gap to book.</span></p>
      </div>

      <div className={`grid md:grid-cols-12 gap-6`}>
        {showSidebar && (
        <div className="md:col-span-4 space-y-6" data-testid="calendar-sidebar">
          <div className="card-tactical rounded-md p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Venue</div>
            <div className="inline-flex rounded-md border border-border overflow-hidden mb-2 w-full" data-testid="calendar-venue-mode">
              {[{ k: "game_day", l: "Game Day venues" }, { k: "all", l: "All" }].map(m => (
                <button key={m.k} type="button" onClick={() => switchVenueMode(m.k)} data-testid={`calendar-venue-mode-${m.k}`}
                  className={`flex-1 px-3 py-1.5 text-[10px] tracking-widest uppercase transition-colors ${venueMode === m.k ? "bg-[#83B68B] text-white" : "hover:bg-muted"}`}>{m.l}</button>
              ))}
            </div>
            <Select value={venueId} onValueChange={setVenueId}>
              <SelectTrigger data-testid="calendar-venue-select">
                <SelectValue placeholder="Choose venue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{gameDayOnly ? "All Game Day venues" : "All venues"}</SelectItem>
                {visibleVenues.map(v => <SelectItem key={v.id} value={v.id}>{v.name} ({(v.pitches||[]).filter(p=>p.active).length})</SelectItem>)}
              </SelectContent>
            </Select>
            {gameDayOnly && visibleVenues.length === 0 && <div className="text-xs text-amber-300 mt-2" data-testid="calendar-no-game-day-venues">No venues are marked "Game Day" yet — an admin can set this on each venue.</div>}
            {!isAll && currentVenue?.address && <div className="text-xs opacity-50 mt-3">{currentVenue.address}</div>}

            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2 mt-5">Pitch size</div>
            <Select value={format_} onValueChange={setFormat}>
              <SelectTrigger data-testid="calendar-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sizes</SelectItem>
                {FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2 mt-5">Availability</div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="calendar-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All slots</SelectItem>
                <SelectItem value="free">Free only</SelectItem>
                <SelectItem value="booked">Booked only</SelectItem>
                {user?.role === "admin" && <SelectItem value="shared" data-testid="calendar-status-shared">Shared trainings only</SelectItem>}
              </SelectContent>
            </Select>

            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2 mt-5">Training season</div>
            <div className="flex flex-wrap gap-1.5" data-testid="calendar-season-chips">
              {[
                { v: "all",    l: "All" },
                { v: "winter", l: "Winter" },
                { v: "summer", l: "Summer" },
              ].map(o => {
                const active = trainingSeason === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setTrainingSeason(o.v)}
                    data-testid={`calendar-season-opt-${o.v}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] tracking-widest uppercase border transition-colors ${
                      active
                        ? "bg-[#83B68B] text-white border-[#83B68B]"
                        : "border-border opacity-90 hover:border-[#83B68B] hover:text-[#83B68B]"
                    }`}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
            <div className="text-[9px] opacity-50 mt-1 leading-relaxed">Hides pitches + training bookings tagged for the other season. Games always show.</div>

            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2 mt-5">Age group</div>
            <div className="flex flex-wrap gap-1.5" data-testid="calendar-age-chips">
              {["all", ...ageOptions].map(a => {
                const active = ageGroup === a;
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAgeGroup(a)}
                    data-testid={a === "all" ? "calendar-age-opt-all" : `calendar-age-opt-${a}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] tracking-widest uppercase border transition-colors ${
                      active
                        ? "bg-[#83B68B] text-white border-[#83B68B]"
                        : "border-border opacity-90 hover:border-[#83B68B] hover:text-[#83B68B]"
                    }`}
                  >
                    {a === "all" ? "All" : a}
                  </button>
                );
              })}
            </div>

            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2 mt-5">Team</div>
            <Select value={teamFilterId} onValueChange={setTeamFilterId}>
              <SelectTrigger data-testid="calendar-team-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                {allTeams
                  .filter(t => ageGroup === "all" ? true : t.age_group === ageGroup)
                  .map(t => <SelectItem key={t.id} value={t.id} data-testid={`calendar-team-opt-${t.id}`}>{t.name} · {t.age_group}</SelectItem>)}
              </SelectContent>
            </Select>
            {(ageGroup !== "all" || teamFilterId !== "all") && (
              <button
                onClick={() => { setAgeGroup("all"); setTeamFilterId("all"); }}
                data-testid="calendar-clear-filters"
                className="mt-3 text-[10px] tracking-widest uppercase w-full px-2.5 py-1.5 border border-border hover:border-red-400 hover:text-red-400 rounded transition-colors"
              >Clear age/team filter</button>
            )}
          </div>
          <div className="card-tactical rounded-md p-3 md:p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Date</div>
            <div className="flex items-center justify-between gap-1 mb-3" data-testid="calendar-day-nav">
              <button
                type="button"
                onClick={() => setDate(subDays(date, 1))}
                data-testid="calendar-prev-day"
                aria-label="Previous day"
                title="Previous day"
                className="p-2 rounded-md border border-border hover:border-[#83B68B] hover:text-[#83B68B] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button
                type="button"
                onClick={() => setDate(new Date())}
                data-testid="calendar-today-btn"
                disabled={isToday(date)}
                className="flex-1 min-w-0 px-2 py-1.5 text-center rounded-md border border-border hover:border-[#83B68B] hover:text-[#83B68B] transition-colors text-xs tracking-wider disabled:opacity-40 disabled:cursor-default"
              >
                <div className="font-display font-bold text-sm truncate">{format(date, "EEE d MMM")}</div>
                <div className="text-[9px] tracking-widest uppercase opacity-60 mt-0.5">{isToday(date) ? "Today" : "Jump to today"}</div>
              </button>
              <button
                type="button"
                onClick={() => setDate(addDays(date, 1))}
                data-testid="calendar-next-day"
                aria-label="Next day"
                title="Next day"
                className="p-2 rounded-md border border-border hover:border-[#83B68B] hover:text-[#83B68B] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            <div className="flex justify-center overflow-x-auto -mx-1">
              <Calendar mode="single" selected={date} onSelect={(d)=>d && setDate(d)} data-testid="calendar-date-picker" />
            </div>
          </div>
          <div className="card-tactical rounded-md p-5 space-y-2 text-sm">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Team kit legend</div>
            {Object.entries(TEAM_COLOR).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3"><span className={`w-4 h-4 rounded ${v.bg} ${v.border} border`}></span> <span data-testid={`legend-${k.toLowerCase()}`}>{kitLabels[k] || k}</span></div>
            ))}
            <div className="flex items-center gap-3 pt-2 border-t border-border mt-2"><span className="w-4 h-4 rounded bg-white border border-black/20 dark:bg-[#111827] dark:border-white/10"></span> Free</div>
            <div className="flex items-center gap-3"><span className="w-4 h-4 rounded border border-black/10 dark:border-white/5"></span> Closed</div>
          </div>
        </div>
        )}

        <div className={`min-w-0 ${showSidebar ? "md:col-span-8" : "md:col-span-12"}`}>
          <div className="card-tactical rounded-md p-4 sm:p-5 overflow-hidden">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
              <div className="flex items-baseline gap-3 flex-wrap">
                <button onClick={()=>setShowSidebar(v=>!v)} data-testid="calendar-toggle-sidebar" className="text-[10px] tracking-widest uppercase px-2.5 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors">
                  {showSidebar ? "Hide filters" : "Show filters"}
                </button>
                <div className="inline-flex rounded border border-border overflow-hidden" data-testid="calendar-view-mode">
                  {[["grid", "Grid"], ["agenda", "Agenda"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setViewMode(k)} data-testid={`calendar-view-${k}`}
                      className={`text-[10px] tracking-widest uppercase px-2.5 py-1.5 transition-colors ${viewMode === k ? "bg-[#83B68B] text-white" : "hover:text-[#83B68B]"}`}>{l}</button>
                  ))}
                </div>
                <button type="button" onClick={() => setRefView(v => !v)} data-testid="calendar-ref-view-toggle" aria-pressed={refView}
                  className={`text-[10px] tracking-widest uppercase px-2.5 py-1.5 border rounded transition-colors ${refView ? "bg-[#83B68B] text-white border-[#83B68B]" : "border-border hover:border-[#83B68B] hover:text-[#83B68B]"}`}>
                  Ref view
                </button>
                <div>
                  <div className="font-display font-black text-2xl tracking-tight">
                    {isAll ? (gameDayOnly ? "Game Day venues" : "All venues") : (currentVenue?.name || "—")}
                  </div>
                  <div className="text-xs opacity-50 mt-0.5">{format(date, "EEEE, d MMMM yyyy")}</div>
                </div>
              </div>
              {!showSidebar && (
                <div className="flex items-center gap-1 w-full sm:w-auto" data-testid="calendar-quick-day-nav">
                  <button type="button" onClick={() => setDate(subDays(date, 1))} data-testid="calendar-quick-prev-day" aria-label="Previous day" className="p-2 rounded-md border border-border hover:border-[#83B68B]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button type="button" onClick={() => setDate(new Date())} disabled={isToday(date)} data-testid="calendar-quick-today" className="flex-1 sm:flex-none px-3 py-1.5 rounded-md border border-border text-xs font-display font-bold disabled:opacity-40">{isToday(date) ? "Today" : format(date, "EEE d MMM")}</button>
                  <button type="button" onClick={() => setDate(addDays(date, 1))} data-testid="calendar-quick-next-day" aria-label="Next day" className="p-2 rounded-md border border-border hover:border-[#83B68B]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              )}
              <div className="flex items-center gap-4 text-xs opacity-60">
                {summary && <span data-testid="calendar-slot-summary">{Math.round(summary.free/4)} h free · {Math.round(summary.booked/4)} h booked</span>}
                {filtered && <span data-testid="calendar-window">{String(filtered.open_hour).padStart(2,"0")}:00 — {String(filtered.close_hour).padStart(2,"0")}:00 <span className="opacity-50 ml-1">· {[0,6].includes(date.getDay()) ? "Weekend" : "Midweek"} window</span></span>}
              </div>
            </div>
            {refView && refTotals.length > 0 && (
              <div className="mb-3 rounded-md border border-[#83B68B]/40 bg-[#83B68B]/5 px-3 py-2" data-testid="ref-day-totals">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">Referee payments · {format(date, "EEE d MMM")} · total £{refTotals.reduce((a, r) => a + r.total, 0).toFixed(2)}</div>
                <div className="flex flex-wrap gap-2">
                  {refTotals.map(r => (
                    <span key={r.name} className="text-xs px-2 py-1 rounded border border-border bg-background" data-testid="ref-day-total">
                      <strong>{r.name}</strong> · {r.games} game{r.games === 1 ? "" : "s"} · <span className="text-[#83B68B] font-bold">£{r.total.toFixed(2)}</span>{r.missing > 0 && <span className="text-amber-300 ml-1">({r.missing} no fee set)</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {loading || !filtered ? (
              closedNotice ? (
                <div className="py-14 px-6 text-center border border-amber-500/30 bg-amber-500/5 rounded-md" data-testid="venue-closed-notice">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300 mb-2">Venue closed</div>
                  <div className="text-sm max-w-md mx-auto opacity-90">{closedNotice}</div>
                  <div className="mt-3 text-[10px] tracking-widest uppercase opacity-50">Change the date or pick a different venue.</div>
                </div>
              ) : (
                <div className="py-16 text-center opacity-50 font-display tracking-widest">LOADING…</div>
              )
            ) : filtered.rows.length === 0 ? (
              <div className="py-16 text-center opacity-50">No pitches match these filters.</div>
            ) : viewMode === "agenda" ? (
              <AgendaView rows={filtered.rows} isAll={isAll} colorFor={colorFor} refView={refView} isMine={isMine} onOpenBooking={setOpenId} onBookSlot={openNewBooking} canBook={!["member", "vendor"].includes(user?.role)} />
            ) : (
              <div
                className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:thin]"
                data-testid="venue-calendar-grid"
                // CSS var used by the .hour-sep utility below so the divider colour swaps
                // with the theme in one place. Bright + heavy = obvious hour boundaries.
                style={{ "--hour-sep": "rgba(0,0,0,0.85)", WebkitOverflowScrolling: "touch" }}
              >
                <style>{`.dark .hour-sep { border-left-color: rgba(255,255,255,0.55) !important; }
                .vcal-pin { position: sticky; left: 0; z-index: 2; }
                .vcal-pin-bg { background: hsl(var(--card)); }
                .vcal-pin::after { content: ""; position: absolute; top: 0; bottom: 0; right: -8px; width: 8px; background: linear-gradient(to right, rgba(0,0,0,0.25), transparent); pointer-events: none; }`}</style>
                <div className="sm:hidden text-[10px] tracking-widest uppercase opacity-50 mb-2" data-testid="calendar-swipe-hint">Swipe sideways to see later times →</div>
                <div className="min-w-[720px] sm:min-w-[900px]">
                  {/* Hour header — each hour spans 4 quarter columns. Vertical dividers
                      between hour blocks make the hour boundary obvious at a glance. */}
                  <div className="grid" style={{ gridTemplateColumns: gridCols }}>
                    <div className="vcal-pin vcal-pin-bg text-[10px] tracking-widest uppercase opacity-50 pb-3">Pitch</div>
                    {hours.map((h, idx) => (
                      <div
                        key={h}
                        className={`text-center text-[10px] tracking-wider uppercase opacity-70 pb-3 pt-1 ${idx > 0 ? "border-l-2 border-black/70 dark:border-white/50" : ""}`}
                        style={{ gridColumn: "span 4" }}
                      >{h.toString().padStart(2,"0")}:00</div>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {filtered.rows.map((row, rowIdx) => {
                      const prevRow = rowIdx > 0 ? filtered.rows[rowIdx - 1] : null;
                      const showVenueHeader = isAll && row.pitch.venue_name && (!prevRow || prevRow.pitch.venue_name !== row.pitch.venue_name);
                      // Collapse only booked runs and closed runs; keep OPEN quarters individual
                      // so drag-select can target every 15-min block
                      const segments = [];
                      let i = 0;
                      while (i < row.slots.length) {
                        const s = row.slots[i];
                        let j = i + 1;
                        if (s.booked && s.booking_id) {
                          while (j < row.slots.length && row.slots[j].booking_id === s.booking_id) j++;
                        } else if (!s.open) {
                          while (j < row.slots.length && !row.slots[j].open) j++;
                        } else {
                          j = i + 1; // single-cell open slot
                        }
                        segments.push({ start: s, span: j - i, dim: s._dim });
                        i = j;
                      }
                      return (
                        <div key={row.pitch.id}>
                          {showVenueHeader && (
                            <div className="mt-3 mb-1 pt-2 border-t border-[#83B68B]/30 flex items-baseline gap-3" data-testid={`venue-group-${row.pitch.venue_name}`}>
                              <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Venue</div>
                              <div className="font-display font-bold text-sm tracking-tight">{row.pitch.venue_name}</div>
                            </div>
                          )}
                          <div className="grid items-stretch" style={{ gridTemplateColumns: gridCols }}>
                            <div className="vcal-pin vcal-pin-bg pr-3 flex flex-col justify-center py-1">
                            <div className="font-display font-bold text-sm tracking-tight truncate">{row.pitch.name}</div>
                            <div className="text-[10px] tracking-widest uppercase opacity-40">
                              {row.pitch.surface}
                              {row.pitch.game_format ? ` · ${row.pitch.game_format}` : ""}
                            </div>
                          </div>
                          {segments.map((seg, idx) => {
                            const s = seg.start;
                            const c = colorFor(s);
                            // drag selection highlight for open cells
                            const inDrag = drag && drag.pitchId === row.pitch.id && s.open && !s.booked && (() => {
                              const [a, b] = drag.startMin <= drag.currentMin ? [drag.startMin, drag.currentMin] : [drag.currentMin, drag.startMin];
                              return s.minute >= a && s.minute <= b;
                            })();
                            const curfewH = row.pitch.league_curfew_hour;
                            const inCurfew = curfewH != null && s.open && !s.booked && s.hour < curfewH && user?.role !== "admin";
                            const cls = !s.open ? "bg-transparent border border-black/10 dark:border-white/5"
                              : s.booked ? `${c.bg} ${c.text} ${c.border} border ${isMine(s) ? "booking-pulse" : ""}`
                              : inDrag ? "bg-[#83B68B]/70 border border-[#83B68B]"
                              : inCurfew ? "vcal-curfew border border-black/20 dark:border-white/10 hover:border-amber-400"
                              : "bg-white border border-black/20 dark:bg-[#111827] dark:border-white/10 hover:border-[#83B68B] dark:hover:border-[#83B68B]";
                            const dim = seg.dim ? "opacity-25" : "";
                            const durationMin = seg.span * 15;
                            const wide = seg.span >= 4;    // 60+ min → show 2 lines of info
                            const superWide = seg.span >= 6; // 90+ min → include opponent
                            // A heavier left border marks the START of each new hour (when
                            // the segment lands on min=0 and isn't the very first cell).
                            // Using inline style to guarantee override — Tailwind arbitrary
                            // border-colors sometimes lose to the `border-black/20` above.
                            const hourStart = s.min === 0 && s.minute !== (filtered.open_hour || 0) * 60;
                            const hourStartStyle = hourStart ? { borderLeftWidth: "3px", borderLeftColor: "var(--hour-sep, currentColor)" } : null;
                            const title = s.booked
                              ? `${s.age_group ? s.age_group + " " : ""}${s.team_name || ""}${s.opponent ? " vs " + s.opponent : ""}${s.match_type ? " · " + s.match_type : ""}${s.session_type ? " · " + s.session_type : ""} · ${durationMin} min`
                              : (s.open ? "Free" : "Closed");
                            const endMin = (s.hour * 60 + s.min + durationMin);
                            const endLabel = `${String(Math.floor(endMin/60)).padStart(2,"0")}:${String(endMin%60).padStart(2,"0")}`;
                            const startLabel = `${String(s.hour).padStart(2,"0")}:${String(s.min).padStart(2,"0")}`;
                            // A booking is draggable only if this user can amend it (booker/admin/YGL).
                            // The grid response doesn't include ownership per slot, so we let the
                            // backend reject on drop and let admins drag anything.
                            const canDragBooking = s.booked && user && (user.role === "admin" || user.year_group_lead_ages?.length > 0);
                            const isDropTarget = dragOver && dragOver.pitchId === row.pitch.id && dragOver.startMin === s.minute;
                            const dropCls = isDropTarget ? "ring-2 ring-[#83B68B] ring-offset-1 ring-offset-[#0A0F1C]" : "";
                            return (
                              <div
                                key={`${row.pitch.id}-${s.minute}`}
                                data-testid={s.booking_id ? `vcal-booking-${s.booking_id}` : `vcal-open-${row.pitch.id}-${s.minute}`}
                                data-curfew={inCurfew ? "true" : undefined}
                                title={s.booked ? (canDragBooking ? `${title} · drag to reschedule` : title) : s.open ? (inCurfew ? `No league games before ${String(curfewH).padStart(2, "0")}:00 at this venue (admin only) · training, cup & friendlies OK` : "Click or drag to book · drop a booking here to reschedule") : "Closed"}
                                draggable={!!canDragBooking}
                                onDragStart={(e) => {
                                  if (!canDragBooking) return;
                                  const dur = seg.span * 15;
                                  dragBookingRef.current = {
                                    booking_id: s.booking_id,
                                    duration_min: dur,
                                    from_pitch_id: row.pitch.id,
                                    from_start_min: s.minute,
                                  };
                                  try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", s.booking_id); } catch {}
                                }}
                                onDragEnd={() => { dragBookingRef.current = null; setDragOver(null); }}
                                onDragOver={(e) => {
                                  const ref = dragBookingRef.current;
                                  if (!ref) return;
                                  // Only allow drop onto an OPEN cell (not booked, not closed)
                                  if (!s.open || s.booked) return;
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = "move";
                                  if (!dragOver || dragOver.pitchId !== row.pitch.id || dragOver.startMin !== s.minute) {
                                    setDragOver({ pitchId: row.pitch.id, startMin: s.minute });
                                  }
                                }}
                                onDrop={async (e) => {
                                  const ref = dragBookingRef.current;
                                  dragBookingRef.current = null;
                                  setDragOver(null);
                                  if (!ref) return;
                                  if (!s.open || s.booked) return;
                                  e.preventDefault();
                                  if (ref.from_pitch_id === row.pitch.id && ref.from_start_min === s.minute) return; // no-op
                                  const dstr = format(date, "yyyy-MM-dd");
                                  const newStart = s.minute;
                                  const newEnd = newStart + ref.duration_min;
                                  try {
                                    await api.put(`/bookings/${ref.booking_id}`, {
                                      pitch_id: row.pitch.id,
                                      booking_date: dstr,
                                      start_hour: Math.floor(newStart / 60),
                                      start_min: newStart % 60,
                                      end_hour: Math.floor(newEnd / 60),
                                      end_min: newEnd % 60,
                                      drag_move: true,
                                    });
                                    toast.success("Booking rescheduled");
                                    const { data: fresh } = await api.get("/venues/availability", { params: { venue_id: venueId, date_str: dstr } });
                                    setData(fresh);
                                  } catch (err) {
                                    toast.error(formatApiError(err.response?.data?.detail) || "Reschedule failed");
                                  }
                                }}
                                onMouseDown={(e) => {
                                  if (!s.open || s.booked) return;
                                  e.preventDefault();
                                  setDrag({ pitchId: row.pitch.id, startMin: s.minute, currentMin: s.minute });
                                }}
                                onMouseEnter={() => {
                                  if (drag && drag.pitchId === row.pitch.id && s.open && !s.booked) {
                                    setDrag(d => ({ ...d, currentMin: s.minute }));
                                  }
                                }}
                                onClick={(e) => {
                                  // If a drag happened across multiple cells, mouseup listener handles it — skip click open
                                  if (drag && drag.startMin !== drag.currentMin) return;
                                  if (s.booking_id) setOpenId(s.booking_id);
                                  else if (s.open) openNewBooking(row.pitch, s);
                                }}
                                className={`h-12 ${cls} ${dim} ${dropCls} ${hourStart ? "hour-sep" : ""} rounded flex flex-col items-start justify-center px-2 overflow-hidden text-[10px] font-display tracking-wider transition-opacity select-none ${(s.booking_id || (s.open && !s.booked)) ? "cursor-pointer hover:brightness-110" : ""} ${canDragBooking ? "cursor-move" : ""}`}
                                style={hourStartStyle ? { gridColumn: `span ${seg.span}`, ...hourStartStyle } : { gridColumn: `span ${seg.span}` }}
                              >
                                {s.booked && (
                                  <>
                                    <div className="flex items-center gap-1.5 w-full leading-tight">
                                      {s.age_group && <span className="font-bold text-[11px]">{s.age_group}</span>}
                                      <span className="font-bold text-[11px] truncate">{s.team_name || (s.match_type || "Booked")}</span>
                                      {s.confirmation_status === "awaiting" && (
                                        <span
                                          data-testid={`slot-awaiting-${s.booking_id}`}
                                          title="Awaiting external venue confirmation"
                                          className="inline-flex items-center px-1 py-0 h-4 rounded-full text-[9px] font-black tracking-wider bg-amber-500/25 text-amber-200 border border-amber-500/70 flex-shrink-0"
                                        >!</span>
                                      )}
                                      {s.shared_count > 0 && (
                                        <span
                                          data-testid={`slot-shared-${s.booking_id}`}
                                          title={`Sharing pitch with ${s.shared_count} other team${s.shared_count > 1 ? "s" : ""}`}
                                          className="ml-auto inline-flex items-center px-1.5 py-0 h-4 rounded-full text-[9px] font-black tracking-wider bg-black/70 text-[#83B68B] border border-[#83B68B]/70 flex-shrink-0"
                                        >+{s.shared_count}</span>
                                      )}
                                      {isFAImported(s) && seg.span >= 2 && (
                                        <FABadge variant="solid" size="xs" className={`${s.shared_count > 0 ? "" : "ml-auto"} flex-shrink-0`} />
                                      )}
                                    </div>
                                    {refView && s.session_type === "game" && (
                                      <div data-testid={`slot-ref-${s.booking_id}`} className={`w-full leading-tight mt-0.5 text-[10px] font-bold truncate ${s.referee_name ? "" : "text-amber-300"}`}>
                                        {s.referee_name ? `Ref: ${s.referee_name}` : s.referee_tbd ? "Ref: TBA" : "Ref: none"}{s.referee_name && s.referee_fee != null && <span className="text-[#83B68B]" data-testid={`slot-ref-fee-${s.booking_id}`}> · £{Number(s.referee_fee).toFixed(2)}</span>}
                                      </div>
                                    )}
                                    {wide && (
                                      <div className="flex items-center gap-1.5 w-full opacity-90 leading-tight mt-0.5">
                                        {s.match_type && <span className="text-[9px] uppercase tracking-widest">{s.match_type}</span>}
                                        {superWide && s.opponent && <span className="text-[9px] opacity-80 truncate">vs {s.opponent}</span>}
                                        <span className="text-[9px] opacity-70 ml-auto">{startLabel}–{endLabel}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BookingDetailDialog
        openId={openId} setOpenId={setOpenId}
        detail={detail} detailLoading={detailLoading}
        kitLabels={kitLabels} user={user}
        teams={teams} allTeams={allTeams}
        pitches={pitches} referees={referees} refereeBusy={refereeBusy}
        onSeriesPreview={async (seriesId, patch) => {
          try {
            const { data } = await api.post(`/bookings/series/${seriesId}/preview`, patch);
            return data;
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
            return null;
          }
        }}
        onSeriesAmend={async (seriesId, patch) => {
          try {
            const { data: res } = await api.patch(`/bookings/series/${seriesId}`, patch);
            toast.success(`Series updated · ${res.updated} future booking(s) changed`);
            setOpenId(null);
            const dstr = format(date, "yyyy-MM-dd");
            const { data: fresh } = await api.get("/venues/availability", { params: { venue_id: venueId, date_str: dstr } });
            setData(fresh);
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Series amend failed"); }
        }}
        onSeriesCancel={async (seriesId) => {
          try {
            const { data: res } = await api.post(`/bookings/series/${seriesId}/cancel`);
            toast.success(`Series cancelled · ${res.cancelled} future booking(s) removed`);
            setOpenId(null);
            const dstr = format(date, "yyyy-MM-dd");
            const { data: fresh } = await api.get("/venues/availability", { params: { venue_id: venueId, date_str: dstr } });
            setData(fresh);
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Cancel series failed"); }
        }}
        onCancel={async (id) => {
          if (!window.confirm("Cancel this booking?")) return;
          try {
            const b = detail;
            await api.delete(`/bookings/${id}`);
            toast.success("Booking cancelled");
            setOpenId(null);
            const dstr = format(date, "yyyy-MM-dd");
            const { data: fresh } = await api.get("/venues/availability", { params: { venue_id: venueId, date_str: dstr } });
            setData(fresh);
            if (b && b.session_type === "game" && (b.match_type === "league" || b.match_type === "cup") && secretaryEnabled) {
              setSecretaryNotice({ booking: b, kind: "cancel" });
            }
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Cancel failed"); }
        }}
        onAmend={async (id, patch) => {
          try {
            const b = detail;
            await api.put(`/bookings/${id}`, patch);
            toast.success("Booking updated");
            setOpenId(null);
            const dstr = format(date, "yyyy-MM-dd");
            const { data: fresh } = await api.get("/venues/availability", { params: { venue_id: venueId, date_str: dstr } });
            setData(fresh);
            const timeChanged = b && (patch.booking_date !== b.booking_date
              || patch.start_hour !== b.start_hour || (patch.start_min ?? 0) !== (b.start_min ?? 0));
            if (b && b.session_type === "game" && (b.match_type === "league" || b.match_type === "cup") && timeChanged && secretaryEnabled) {
              setSecretaryNotice({ booking: b, kind: "amend", patch });
            }
          } catch (err) {
            const d = err.response?.data?.detail;
            if (err.response?.status === 409 && d?.team_clash && user?.role === "admin") throw err;
            toast.error(formatApiError(d) || "Amend failed");
          }
        }}
        onNudgeReferee={async (id) => {
          if (!window.confirm("Email the booking coach to remind them to assign a referee?")) return;
          try {
            const { data: res } = await api.post(`/bookings/${id}/nudge-referee-coach`);
            toast.success(`Nudge sent to ${res.notified}`);
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Nudge failed"); }
        }}
      />
      <NewBookingDialog
        newSlot={newSlot} setNewSlot={(v) => { setNewSlot(v); if (!v) setClashDetail(null); }}
        nb={nb} setNb={setNb} nbBusy={nbBusy}
        submitNewBooking={submitNewBooking}
        date={date} user={user} teams={teams}
        eligibleReferees={eligibleReferees} refereeBusy={refereeBusy}
        showAllTeams={showAllTeams} setShowAllTeams={setShowAllTeams}
        clashDetail={clashDetail} onDismissClash={() => setClashDetail(null)}
      />
      <SecretaryNoticeDialog
        open={!!secretaryNotice}
        booking={secretaryNotice?.booking}
        kind={secretaryNotice?.kind}
        onClose={() => setSecretaryNotice(null)}
      />
    </div>
  );
}
